// PWA офлайн-кеш на Dexie/IndexedDB. ЛЕНИВО: чтобы не тянуть Dexie (~95 КБ
// minified) в initial bundle, top-level import заменён на type-only, а сама
// инициализация инстанса — async через getDb(). Это снимает ~500 мс с TBT
// и разгружает main-thread на первой загрузке /dashboard.
//
// См. notes/PERF_DASHBOARD_PLAN.md → шаг 2 (Dexie lazy-import).
//
// Правило: НИКАКОГО прямого `import { db } from './db'` снаружи. Всё через
// getDb() или helper-функции из offlineStore.ts. Если когда-нибудь захочется
// синхронный db.* — значит надо подумать ещё раз: lazy теряется.

import type { Dexie, EntityTable } from 'dexie';
import type { EntryFile, EntryMedia, MusicLinks, StoreLinks, UnsplashAttribution } from '@/types';

// ---- Cached data types ----

export interface CachedEntry {
  id: string;
  user_id: string;
  chat_id: string;
  /** Привязка к общему списку чата (Phase 5 polish). NOT NULL → запись общая.
   *  В IDB-кеше эти записи лежат рядом с личными — отличаются по этому полю.
   *  Нужно для chip-фильтра по имени категории (общая категория триггерера
   *  не совпадает с category_id текущего юзера, но имя то же). */
  chat_group_id?: string | null;
  message_text?: string;
  url?: string;
  title?: string;
  description?: string;
  body?: string;
  content_type?: string;
  image_url?: string;
  /** Натуральные размеры обложки в пикселях. Для резервирования места
   *  под обложку (aspect-ratio контейнера) → ноль CLS. NULL для старых
   *  записей и тех источников, где размер недоступен. Заполняется либо
   *  серверным pipeline (TG/VK photo size, og:image:width/height), либо
   *  клиентским onLoad-кешем в EntryCard. Шаг 2.1 PERF_DASHBOARD_PLAN.md. */
  image_width?: number | null;
  image_height?: number | null;
  /** Атрибуция Unsplash-фото (когда обложка взята из Unsplash). Нужна для
   *  показа на /item/[id] — Unsplash требует ссылку на автора. */
  image_attribution?: UnsplashAttribution | null;
  telegram_file_id?: string;
  /** TG file_id для видео-обложки. Используется кнопкой ▶ в галерее /item. */
  video_file_id?: string;
  /** Канонический watch URL (VK video / Kinopoisk). Кнопка «Смотреть онлайн». */
  watch_url?: string;
  category: string;
  category_id?: string | null;
  /** Emoji категории (для vote-режима, когда смотрим чужую запись и в своих
   *  категориях этой нет — резолвер не найдёт). Phase 1 PWA: приводим к
   *  parity с Entry, чтобы /item/[id] offline-fallback мог взять отсюда. */
  category_emoji?: string;
  /** Места (📍-категории) — для deep links «Открыть в Google Maps / Я.Картах»
   *  на /item/[id]. */
  place_lat?: number | null;
  place_lng?: number | null;
  place_address?: string | null;
  /** Магазинные ссылки для игр (категория 🎮). Кешируется чтобы офлайн-/item
   *  показывал кнопки Steam/Google Play/App Store. NULL = не пытались. */
  store_links?: StoreLinks | null;
  /** Стриминговые ссылки для музыки (категория 🎵): Я.Музыка, Spotify, Apple
   *  Music. Кешируется чтобы офлайн-/item показывал кнопки. NULL = не пытались. */
  music_links?: MusicLinks | null;
  /** Имя оригинального автора, если entry создан reply-trigger'ом в группе.
   *  Phase 4 GROUP_MODES_PLAN.md. Показывается «автор сообщения: Маша». */
  source_author_display?: string | null;
  /** Ссылка на оригинал, если запись — копия (лайк в voting). NULL = это
   *  оригинал. Нужно чтобы /item подтягивал комменты с оригинала. */
  source_entry_id?: string | null;
  tags: string[];
  created_at: string;
  // from entry_statuses
  is_read?: boolean;
  decision?: string;
  reaction?: string;
  never_at?: string;
  delete_at?: string;
  status_updated_at?: string;
  // from chats
  chat_title?: string;
  // от mapEntry — счётчик прикреплённых файлов для индикатора 📎N на карточке
  files_count?: number;
  /** Полный список прикреплённых файлов. Хранится только для записей которые
   *  юзер открывал онлайн (попадает через cacheEntryById → GET /api/entries/[id]
   *  с JOIN entry_files(*)). FullCatalogSync через /api/entries/all тянет
   *  только files_count, не full files[] — оптимизация трафика для каталога
   *  до 5000 записей. Phase 1 PWA офлайн. */
  files?: EntryFile[];
  /** Галерея альбома (entry_media). Те же правила что и files: попадает
   *  через cacheEntryById, не через FullCatalogSync (тяжело по трафику).
   *  Используется EntryMediaGallery на /item/[id]. */
  media?: EntryMedia[];
  // cache metadata
  _cached_at: number;
}

export interface CachedCategory {
  id: string;
  user_id: string;
  name: string;
  emoji: string;
  is_visible: boolean;
  /** Режим категории: 'queue' (default) | 'collection'. Может отсутствовать в
   *  старом кеше до первого пересинка — трактуется как 'queue'. */
  mode?: 'queue' | 'collection';
  sort_order?: number;
  _cached_at: number;
}

export type PendingOpType = 'create' | 'update' | 'delete';

export interface PendingOperation {
  id?: number; // auto-incremented
  type: PendingOpType;
  table: 'entries' | 'entry_statuses' | 'categories';
  entity_id: string;
  payload: Record<string, unknown>;
  created_at: number;
}

// ---- Database (lazy singleton) ----

export type FavboxDB = Dexie & {
  entries: EntityTable<CachedEntry, 'id'>;
  categories: EntityTable<CachedCategory, 'id'>;
  pendingOps: EntityTable<PendingOperation, 'id'>;
};

let _db: FavboxDB | null = null;
let _dbPromise: Promise<FavboxDB> | null = null;

/**
 * Лениво инициализирует Dexie (динамический import) и возвращает singleton
 * инстанс. При параллельных вызовах все ждут одного и того же promise — без
 * race-condition с двумя Dexie-объектами.
 *
 * Бросает если динамический import упал (CSP, network). Caller'ы в
 * offlineStore/offlineQueue имеют свои try/catch — кеш-операция тихо
 * превратится в no-op, юзер на проде ничего не заметит.
 */
export async function getDb(): Promise<FavboxDB> {
  if (_db) return _db;
  if (_dbPromise) return _dbPromise;

  _dbPromise = (async () => {
    const DexieClass = (await import('dexie')).default;
    const db = new DexieClass('favbox') as FavboxDB;
    db.version(1).stores({
      entries: 'id, user_id, category, category_id, created_at, decision, reaction',
      categories: 'id, user_id, name, sort_order',
      pendingOps: '++id, type, table, entity_id, created_at',
    });
    _db = db;
    return db;
  })();

  return _dbPromise;
}

/**
 * Откладывает выполнение функции до момента когда браузер свободен. Под капотом
 * `requestIdleCallback` где он есть (Chrome/Edge/Firefox), иначе `setTimeout`
 * на 1с (Safari) — этого достаточно чтобы основной поток успел отрисовать
 * first paint / LCP / interactive до того, как мы начнём тяжёлую работу.
 *
 * Использовать для фоновой инициализации, которая НЕ должна блочить first
 * paint: FullCatalogSync, SyncManager flush, prefetchIdb. Шаг 2.2
 * PERF_DASHBOARD_PLAN.md.
 *
 * В SSR — no-op (нет browser API).
 */
export function runWhenIdle(fn: () => void): void {
  if (typeof window === 'undefined') return;
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(fn);
  } else {
    setTimeout(fn, 1000);
  }
}

/**
 * Прелоадит Dexie в idle-окне после первого paint, чтобы первый клик юзера
 * по фильтру / кнопке не ждал ~500 мс скачивания и парсинга 95 КБ.
 *
 * Безопасно вызывать многократно — getDb() имеет singleton-guard.
 * Безопасно вызывать в SSR — runWhenIdle делает no-op в server-env.
 */
export function prefetchIdb(): void {
  runWhenIdle(() => {
    getDb().catch(() => {
      // Dexie не догрузился — ничего страшного, попробуем при реальном
      // обращении. Catch важен чтобы не утечь в unhandled-rejection.
    });
  });
}
